import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import PropertyCard from "@/components/PropertyCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 6;

export default function PropertiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    type: searchParams.get("type") || "all",
    search: searchParams.get("search") || "",
    minPrice: "",
    maxPrice: "",
  });

  const fetchProperties = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (filters.type && filters.type !== "all") params.property_type = filters.type;
      if (filters.search) params.search = filters.search;
      if (filters.minPrice) params.min_price = parseFloat(filters.minPrice);
      if (filters.maxPrice) params.max_price = parseFloat(filters.maxPrice);

      const res = await axios.get(`${API}/properties`, { params });
      setProperties(res.data.properties || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.page || 1);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error("Failed to fetch properties:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProperties(1);
  };

  const handleTypeChange = (value) => {
    setFilters((prev) => ({ ...prev, type: value }));
    setCurrentPage(1);
    const params = { page: 1, per_page: PER_PAGE };
    if (value !== "all") params.property_type = value;
    if (filters.search) params.search = filters.search;
    if (filters.minPrice) params.min_price = parseFloat(filters.minPrice);
    if (filters.maxPrice) params.max_price = parseFloat(filters.maxPrice);

    axios.get(`${API}/properties`, { params }).then((res) => {
      setProperties(res.data.properties || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    });

    setSearchParams(value !== "all" ? { type: value } : {});
  };

  const clearFilters = () => {
    setFilters({ type: "all", search: "", minPrice: "", maxPrice: "" });
    setSearchParams({});
    setCurrentPage(1);
    axios.get(`${API}/properties`, { params: { page: 1, per_page: PER_PAGE } }).then((res) => {
      setProperties(res.data.properties || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    });
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    fetchProperties(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const typeLabels = {
    all: "All Properties",
    villa: "Villas",
    flat: "Apartments",
    land: "Land Plots",
  };

  return (
    <div data-testid="properties-page" className="pt-24 pb-16 min-h-screen">
      {/* Header */}
      <div className="px-6 lg:px-8 max-w-7xl mx-auto mb-12">
        <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">Our Collection</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter leading-none text-beige mb-4">
          Properties in South Goa
        </h1>
        <p className="text-beige/60 max-w-lg">
          Browse our curated selection of premium properties across South Goa's most coveted locations.
        </p>
      </div>

      {/* Filters */}
      <div className="px-6 lg:px-8 max-w-7xl mx-auto mb-10">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 w-full md:max-w-md">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-beige/40" />
              <Input
                data-testid="property-search-input"
                type="text"
                placeholder="Search by location, title..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="pl-10 bg-navy-light border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50 focus:ring-gold/20"
              />
            </div>
          </form>

          {/* Type Filter */}
          <Select value={filters.type} onValueChange={handleTypeChange}>
            <SelectTrigger
              data-testid="property-type-filter"
              className="w-full md:w-48 bg-navy-light border-white/10 text-beige rounded-sm h-11"
            >
              <SelectValue placeholder="Property Type" />
            </SelectTrigger>
            <SelectContent className="bg-navy-light border-white/10">
              {Object.entries(typeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value} className="text-beige hover:bg-navy focus:bg-navy focus:text-gold">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Toggle Advanced Filters */}
          <button
            data-testid="toggle-filters-button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-beige/60 hover:text-gold transition-colors border border-white/10 px-4 py-2.5 rounded-sm"
          >
            <SlidersHorizontal size={14} /> Filters
          </button>

          {(filters.type !== "all" || filters.search || filters.minPrice || filters.maxPrice) && (
            <button
              data-testid="clear-filters-button"
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-gold hover:text-gold-hover transition-colors"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div data-testid="advanced-filters" className="mt-4 flex flex-col sm:flex-row gap-4 p-4 bg-navy-light border border-white/10 rounded-sm">
            <div className="flex-1">
              <label className="text-xs text-beige/50 mb-1 block">Min Price (Cr)</label>
              <Input
                data-testid="min-price-filter"
                type="number"
                step="0.1"
                placeholder="0"
                value={filters.minPrice}
                onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
                className="bg-navy border-white/10 text-beige rounded-sm h-10 placeholder:text-beige/30"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-beige/50 mb-1 block">Max Price (Cr)</label>
              <Input
                data-testid="max-price-filter"
                type="number"
                step="0.1"
                placeholder="50"
                value={filters.maxPrice}
                onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
                className="bg-navy border-white/10 text-beige rounded-sm h-10 placeholder:text-beige/30"
              />
            </div>
            <div className="flex items-end">
              <button
                data-testid="apply-filters-button"
                onClick={() => { setCurrentPage(1); fetchProperties(1); }}
                className="bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-wider uppercase px-6 py-2.5 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-beige/40 mt-4">{total} {total === 1 ? "property" : "properties"} found</p>
      </div>

      {/* Grid */}
      <div className="px-6 lg:px-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-navy-light rounded-sm animate-pulse aspect-[3/4]" />
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-beige/40 text-lg mb-4">No properties found</p>
            <button onClick={clearFilters} className="text-gold text-sm hover:text-gold-hover">
              Clear filters and try again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div data-testid="pagination-controls" className="flex items-center justify-center gap-2 mt-12">
            <button
              data-testid="pagination-prev"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="flex items-center gap-1 text-sm text-beige/60 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 px-3 py-2 rounded-sm transition-colors"
            >
              <ChevronLeft size={14} /> Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                data-testid={`pagination-page-${page}`}
                onClick={() => goToPage(page)}
                className={`text-sm w-10 h-10 rounded-sm transition-colors ${
                  page === currentPage
                    ? "bg-gold text-navy font-bold"
                    : "text-beige/60 hover:text-gold border border-white/10"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              data-testid="pagination-next"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="flex items-center gap-1 text-sm text-beige/60 hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed border border-white/10 px-3 py-2 rounded-sm transition-colors"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
